import { useCallback, useMemo } from 'react';
import { useLocation } from 'wouter';

import useMicromagStory from '../../hooks/useMicromagStory';

import Micromag from '../partials/Micromag';
import PageMeta from '../partials/PageMeta';

import styles from '../../styles/pages/micromag.module.css';

interface MicromagPageProps {
    micromag: MicromagItem;
    screen?: string | null;
    hasHome?: boolean;
}

function MicromagPage({ micromag, screen = null, hasHome = false }: MicromagPageProps) {
    const [, setLocation] = useLocation();
    const { slug = null } = micromag || {};
    const { story = null } = useMicromagStory(micromag);
    const { title = null, metadata } = story || {};

    const { components: screens = [] } = story || {};
    const baseUrl = slug !== null && hasHome ? `/${slug}` : '/';

    const pathScreenId = screen || null;
    const pathScreenIndex =
        pathScreenId !== null && pathScreenId.match(/^[0-9]+$/) !== null
            ? parseInt(pathScreenId, 10) - 1
            : null;
    const screenByPathId =
        pathScreenId !== null
            ? screens.find(({ id: screenId = null }) => screenId === pathScreenId) || null
            : null;
    const screenByPathIndex = pathScreenIndex !== null ? screens[pathScreenIndex] || null : null;
    const currentScreen =
        screenByPathId ||
        screenByPathIndex ||
        (pathScreenId === '' ? screens[0] : null) ||
        screens[0] ||
        null;

    const onScreenChange = useCallback(
        (newScreen = null) => {
            const { id: newScreenId = null } = newScreen || {};
            const newScreenIndex =
                screens.findIndex(({ id: screenId = null }) => screenId === newScreenId) || null;
            setLocation(
                newScreenIndex !== null && newScreenIndex > 0
                    ? `${baseUrl.replace(/\/$/, '')}/${newScreenIndex + 1}`
                    : `${baseUrl.replace(/\/$/, '')}/`,
            );
        },
        [screens, setLocation],
    );

    const filteredStory = useMemo(() => {
        const { components: storyScreens = [], ...restStory } = story || {};
        return {
            ...restStory,
            components:
                storyScreens
                    .map((screen) => {
                        const { type, parameters } = screen || {};
                        const { metadata } = parameters || {};
                        const { description = null } = metadata || {};
                        if (type === 'keypad') {
                            const { items = [] } = screen || {};
                            const hasWerther =
                                items.findIndex((item) => {
                                    const { button } = item || {};
                                    const { url = null } = button || {};
                                    return (url || '').indexOf('werther') !== -1;
                                }) !== -1;
                            return hasWerther
                                ? {
                                      ...screen,
                                      items: items.filter((item) => {
                                          const { button } = item || {};
                                          const { url = null } = button || {};
                                          return (url || '').indexOf('werther') === -1;
                                      }),
                                  }
                                : screen;
                        }
                        if (
                            description !== null &&
                            description.toLowerCase().indexOf('werther') !== -1
                        ) {
                            return null;
                        }
                        return screen;
                    })
                    .filter((it) => it !== null) || [],
        };
    }, [story]);

    return (
        <div className={styles.container}>
            <PageMeta title={title} {...metadata} />
            <Micromag
                micromag={filteredStory}
                basePath={baseUrl}
                hasHome={hasHome}
                currentScreenId={currentScreen !== null ? currentScreen.id : undefined}
                className={styles.micromag}
                onScreenChange={onScreenChange}
            />
        </div>
    );
}

export default MicromagPage;
